import { FC, useEffect, useState } from "react";
import Logo from "../../../assets/images/logo.svg";
import Google from "../../../assets/images/gmail-logo.svg";
import {
  Box,
  Checkbox,
  FormControl,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { CustomButton } from "../../../components/Button";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import TextInput from "../../../components/TextInput";
import { Link, useNavigate } from "react-router-dom";
import BotLogo from "../../../assets/images/bot-logo.svg";
import { ReactComponent as EmailIcon } from "../../../assets/images/mail-secure-02.svg";
import { adjustToggle } from "../../../utils/adjustToggle";
import {
  useSignupMutation,
} from "../../../services/auth";
import { useForm } from "react-hook-form";
import { errorToast, successToast } from "../../../utils/toast";
import "./index.css";
import keys from "../../../utils/keys";
const currentYear = new Date().getFullYear();

interface FormValues {
  country_code: string;
  password: string;
  givenName: string;
  familyName: string;
  phone_number: string;
  email: string;
}

const Signup = ({ onSubmit }: { onSubmit?: (data: FormValues) => void }) => {
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [signUpType, setSignUpType] = useState("");
  const [loader, setLoader] = useState(false);
  const [signup, result] = useSignupMutation();
  const [googleSignupLoading, setGoogleSignupLoading] = useState(false);
  const navigate = useNavigate();
  const handleVisibility = () => {
    setShowPassword(!showPassword);
  };
  const [email, setEmail] = useState("");
  const [conditionsCheck, setConditionsCheck] = useState({
    terms: false,
    privacy: false,
  });

  const {
    register,
    resetField,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      country_code: "+1",
      password: "",
      givenName: "",
      familyName: "",
      phone_number: "",
      email: "",
    },
  });

  useEffect(() => {
    adjustToggle();
  }, []);

  const onSubmitHandler = async (data: any) => {
    setLoader(true);
    const {
      password,
      givenName,
      familyName,
      phone_number,
      country_code,
      email,
    } = data;
    const body = {
      password,
      attributes: {
        givenName,
        familyName,
        email,
        phone_number: `${country_code}${phone_number}`,
      },
    };
    setEmail(email);
    const response: any = await signup(body);
    setLoader(false);
    if (response.error) {
      errorToast(
        response.error.error ||
          (response.error.data && response.error.data.message)
      );
    } else {
      if (
        response.data &&
        response.data.result &&
        response.data.result.$metadata &&
        response.data.result.$metadata.httpStatusCode == 200
      ) {
        // successToast(
        //   `${response.data.message}.\n Verification link has been sent to ${email}`,
        //   "top-center",
        //   5000
        // );
        reset();
        // navigate("/signin");
      } else {
        errorToast(response.data.message);
      }
    }
  };

  const checkCondition = (opt: string) => {
    const check = conditionsCheck[opt == "terms" ? "terms" : "privacy"];
    setConditionsCheck({
      ...conditionsCheck,
      [opt]: !check,
    });
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (
    event
  ) => {
    const allowKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"];
    const isDigit = /^[0-9]$/.test(event.key);
    const isAllowed = isDigit || allowKeys.includes(event.key);

    if (!isAllowed) {
      event.preventDefault();
    }
  };

  const googleSignUp = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setGoogleSignupLoading(true);
    
    try {
      // Redirect directly to the backend Google OAuth endpoint
      // The backend will handle the redirect to Google OAuth
      const googleOAuthUrl = `${keys.baseUrl}user/google`;
      window.location.href = googleOAuthUrl;
    } catch (error) {
      console.error("Google signup error:", error);
      errorToast("Failed to initiate Google sign-up. Please try again.");
      setGoogleSignupLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmitHandler)}>
      <Box
        sx={{
          flex: 1,
          marginTop: "3.5%",
        }}
      >
        <img src={BotLogo} alt="Logo" className="bot-logo" />
        <Grid
          container
          spacing={2}
          direction="column"
          alignItems="center"
          justifyContent="center"
        >
          <Grid size={{ xs: 10, md: 3 }} textAlign={"center"}>
            {!result.isSuccess ? (
              <div className="div-class">
                <img
                  src={Logo}
                  alt="Logo"
                  style={{
                    width: "100%",
                    height: "180px",
                    margin: "10% 0px 15px 0px",
                  }}
                />
              </div>
            ) : null}
            <div>
              {!result.isSuccess ? (
                <div className="welcomeText">Welcome 👋</div>
              ) : null}
              <Stack spacing={2}>
                {!result.isSuccess && signUpType == "email" ? (
                  <>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }} textAlign={"center"}>
                        <FormControl variant="standard" fullWidth>
                          <TextInput
                            label="First Name"
                            id="givenName-input"
                            placeholder="Enter first name"
                            ariaLabel="firstname"
                            iconHandler={() => resetField("givenName")}
                            type="text"
                            icon={
                              <HighlightOffIcon
                                sx={{ color: "#2A945F", fontSize: "20px" }}
                              />
                            }
                            register={register}
                            required={true}
                            name="givenName"
                            borderColor={errors.givenName && "#e11c1cb3"}
                            helperText={
                              errors.givenName ? "Required field" : ""
                            }
                          />
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }} textAlign={"center"}>
                        <FormControl variant="standard" fullWidth>
                          <TextInput
                            label="Last Name"
                            id="familyName-input"
                            placeholder="Enter last name"
                            ariaLabel="lastname"
                            iconHandler={() => resetField("familyName")}
                            type="text"
                            icon={
                              <HighlightOffIcon
                                sx={{ color: "#2A945F", fontSize: "20px" }}
                              />
                            }
                            required={true}
                            register={register}
                            name="familyName"
                            borderColor={errors.familyName && "#e11c1cb3"}
                            helperText={
                              errors.familyName ? "Required field" : ""
                            }
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 3 }} textAlign={"center"}>
                        <FormControl variant="standard" fullWidth>
                          <TextInput
                            label="Code"
                            id="country_code-input"
                            placeholder="Enter code"
                            ariaLabel="code"
                            type="text"
                            required={true}
                            register={register}
                            name="country_code"
                            borderColor={errors.country_code && "#e11c1cb3"}
                            helperText={
                              errors.country_code
                                ? errors.country_code.type == "pattern"
                                  ? "Invalid"
                                  : "Required field"
                                : ""
                            }
                            pattern={/^(\+?\d{1,3}|\d{1,4})$/}
                          />
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, md: 9 }} textAlign={"center"}>
                        <FormControl variant="standard" fullWidth>
                          <TextInput
                            label="Phone"
                            id="phone-input"
                            placeholder="Enter phone"
                            ariaLabel="phone"
                            iconHandler={() => resetField("phone_number")}
                            type="text"
                            icon={
                              <HighlightOffIcon
                                sx={{ color: "#2A945F", fontSize: "20px" }}
                              />
                            }
                            maxLength={15}
                            required={true}
                            handleKeyDown={handleKeyDown}
                            register={register}
                            name="phone_number"
                            borderColor={errors.phone_number && "#e11c1cb3"}
                            helperText={
                              errors.phone_number
                                ? errors.phone_number.type == "pattern"
                                  ? "Invalid phone number"
                                  : "Required field"
                                : ""
                            }
                            pattern={/^([0|\+[0-9]{1,5})?([0-9]{10})$/}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                    <FormControl variant="standard" fullWidth>
                      <TextInput
                        label="Email Address"
                        id="email-input"
                        placeholder="Enter a valid email address"
                        ariaLabel="email"
                        iconHandler={() => resetField("email")}
                        borderColor={errors.email && "#e11c1cb3"}
                        icon={
                          <HighlightOffIcon
                            sx={{ color: "#2A945F", fontSize: "20px" }}
                          />
                        }
                        required={true}
                        register={register}
                        name="email"
                        pattern={
                          /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                        }
                        helperText={
                          errors.email
                            ? errors.email.type == "pattern"
                              ? "Invalid email address"
                              : "Required field"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormControl variant="standard" fullWidth>
                      <TextInput
                        label="Password"
                        id="password-input"
                        placeholder="Enter a valid password"
                        type={showPassword ? "text" : "password"}
                        iconHandler={handleVisibility}
                        borderColor={errors.password && "#e11c1cb3"}
                        icon={
                          showPassword ? (
                            <VisibilityOffIcon
                              sx={{ color: "#2A945F", fontSize: "20px" }}
                            />
                          ) : (
                            <VisibilityIcon
                              sx={{ color: "#2A945F", fontSize: "20px" }}
                            />
                          )
                        }
                        pattern={
                          /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,16}$/
                        }
                        required={true}
                        register={register}
                        name="password"
                        helperText={
                          errors.password
                            ? errors.password.type == "pattern"
                              ? `Password must contain at least 8 characters, at least 1 uppercase letter (A-X), at least 1 lowercase letter (a-z), at least 1 number (0-9) and at least 1 special character from this set: !@#$%^&*()-_=+[]{}|;:'",.<>?/.`
                              : "Required field"
                            : ""
                        }
                      />
                      {!errors.password ? (
                        <span style={{ fontSize: "8px", color: "grey" }}>
                          Password should include 8+ characters, uppercase,
                          lowercase, number & special character.
                        </span>
                      ) : null}
                    </FormControl>
                    <CustomButton
                      text={result.isLoading ? "" : "Sign Up"}
                      id="submit-button"
                    />
                  </>
                ) : !result.isSuccess ? (
                  <>
                    <div className="terms">
                      <Checkbox
                        checked={conditionsCheck.terms}
                        sx={{
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                          color: "#2C4A3A",
                        }}
                        size="small"
                        onClick={() => checkCondition("terms")}
                      />
                      <Typography
                        fontSize={"13px"}
                        fontFamily={"Poppins"}
                        marginLeft={"-15px"}
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                        onClick={() =>
                          window.open(
                            "https://hiibo.app/termsandconditions",
                            "_blank"
                          )
                        }
                      >
                        Accept terms and conditions
                      </Typography>
                      <Checkbox
                        checked={conditionsCheck.privacy}
                        sx={{
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                          color: "#2C4A3A",
                        }}
                        size="small"
                        onClick={() => checkCondition("privacy")}
                      />
                      <Typography
                        fontSize={"13px"}
                        fontFamily={"Poppins"}
                        marginLeft={"-15px"}
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                        onClick={() =>
                          window.open(
                            "https://hiibo.app/privacy-policy",
                            "_blank"
                          )
                        }
                      >
                        Privacy policy
                      </Typography>
                    </div>
                    <CustomButton
                      text={"Sign up with Email"}
                      startIcon={<MailOutlineIcon sx={{ color: "#2A945F" }} />}
                      submit={() => setSignUpType("email")}
                      // color="white"
                      height="60px"
                      disabled={
                        !(conditionsCheck.terms && conditionsCheck.privacy)
                      }
                    />
                    <CustomButton
                      text={googleSignupLoading ? "" : "Sign up with Google"}
                      startIcon={
                        !googleSignupLoading ? (
                          <img
                            src={Google}
                            alt="google"
                            style={{ width: "20px", height: "20px" }}
                          />
                        ) : undefined
                      }
                      submit={googleSignUp}
                      // color="white"
                      height="60px"
                      disabled={
                        !(conditionsCheck.terms && conditionsCheck.privacy) || googleSignupLoading
                      }
                    />
                  </>
                ) : (
                  <div className="verification">
                    <EmailIcon
                      className={
                        theme.palette.mode == "dark"
                          ? "light-email"
                          : "dark-email"
                      }
                    />
                    <Typography>
                      Bo sent you an email to{" "}
                      <span style={{ color: "#2a945f" }}>{email}</span> Verify
                      to create your account.
                    </Typography>
                    <CustomButton text={"Resend Email"} />
                  </div>
                )}
                <Typography className="text">
                  Already have an account?{" "}
                  <Link className="signin" to="/signin">
                    Sign in
                  </Link>
                </Typography>
              </Stack>
            </div>
          </Grid>
        </Grid>
      </Box>
      <div className="copyright-div">
        <Typography
          className="copyright"
          sx={{
            color: theme.palette.mode == "dark" ? "#34b47440" : "lightgrey",
          }}
        >
          © {currentYear} ALL RIGHTS RESERVED
        </Typography>
      </div>
    </form>
  );
};

export default Signup;
